import { useContext } from 'react';
import PropTypes from 'prop-types';

import { QUESTION_TYPES } from '@api/instant_survey';

import {
  Checkbox,
  ErrorText,
  Fieldset,
  PillOverlayPopover,
  Radio,
  truncateTextWithEllipsis,
} from '@utilities';

import { SurveyContext } from '../../../../../../utilities/context';

import styles from './_index.module.scss';

const QuestionLogicOptions = ({
  firstErrorRef,
  isCompleted,
  isError,
  isFocused,
  isQuestion = false,
  isQuotaGroup = false,
  isQuotaGroupSelectionValid,
  isRequired,
  isSkip,
  options,
  optionsLabel,
  optionsType,
  setValue,
  value,
}) => {
  const { questionDisplayIndexById } = useContext(SurveyContext);
  const availableOptions = isQuestion
    ? options?.filter(({ type }) =>
        [
          QUESTION_TYPES.CHECKBOX.value,
          QUESTION_TYPES.NUMERIC.value,
          QUESTION_TYPES.RADIO.value,
        ].includes(type)
      )
    : options;

  const getErrorMessage = () => {
    if (isQuotaGroup) {
      if (!isQuotaGroupSelectionValid) {
        return 'At least one of your quota groups used for logic has been removed. Please re-select the group(s) you want or remove this logic.';
      } else if (value.length === 0) {
        return 'Please select at least one quota group.';
      }
    } else {
      if (optionsType === 'checkbox') {
        return 'Please select at least one option.';
      } else if (optionsType === 'radio') {
        return 'Please select an option.';
      }
    }
  };
  const getPillLabel = () => {
    let updatedLabel;
    if (optionsType === 'radio') {
      updatedLabel = value?.text || optionsLabel;
      if (value?.id) {
        // truncation for selected question
        const truncatedText = truncateTextWithEllipsis({ length: 30, text: updatedLabel });
        updatedLabel = `Q${questionDisplayIndexById[value.id]}: "${truncatedText}"`;
      }
    } else {
      updatedLabel = value?.[0]?.text || optionsLabel;
      if ((value?.length ?? 0) > 1) {
        // truncation for multiple selected answers
        const textLimit = isSkip ? 50 : 10;
        if (updatedLabel.length > textLimit) {
          updatedLabel = updatedLabel.substring(0, textLimit) + '...';
        }
        updatedLabel = `"${updatedLabel}" and ${value.length - 1} more`;
      } else {
        // truncation for single selected answer
        const textLimit = isSkip ? 60 : 20;
        if (updatedLabel.length > textLimit) {
          updatedLabel = updatedLabel.substring(0, textLimit) + '...';
        }
      }
    }
    return updatedLabel;
  };

  const toggleCheckbox = (option) => {
    const safeValue = value ?? [];
    const index = safeValue.findIndex((val) => {
      return val.id === option.id;
    });

    if (index === -1) {
      setValue([...safeValue, option]);
    } else {
      const updatedValue = [...safeValue];
      updatedValue.splice(index, 1);
      setValue(updatedValue);
    }
  };

  return (
    <PillOverlayPopover
      buttonRef={isFocused ? firstErrorRef : null}
      isCompleted={isCompleted}
      isError={isError}
      isRequired={isRequired}
      overlayClassName={styles['question-logic-options-overlay']}
      overlayTestId="question-logic-pill-overlay"
      pillClassName={isFocused ? styles['question-logic-options-pill-focus'] : null}
      pillTestId="question-logic-pill"
      tooltipContent={
        optionsType === 'checkbox' && (value?.length ?? 0) > 1 ? (
          <>
            {value.map((option) => {
              return <div key={`tooltip-${option.id}`}>{option.text}</div>;
            })}
          </>
        ) : null
      }
      tooltipPlacement="bottom"
      value={getPillLabel()}
    >
      <Fieldset className={styles['question-logic-options-fieldset']} legend={optionsLabel}>
        <ul className={styles['question-logic-options-list']}>
          {isError && (
            <li>
              <ErrorText>{getErrorMessage()}</ErrorText>
            </li>
          )}
          {availableOptions?.map((option, index) => {
            return (
              <li key={`${option}-${index}`}>
                {optionsType === 'radio' && (
                  <Radio
                    isChecked={value?.id ? value.id === option.id : value?.text === option.text}
                    label={
                      option.id
                        ? `Q${questionDisplayIndexById[option.id]}: "${option.text}"`
                        : option.text
                    }
                    onChange={setValue}
                    value={option}
                  />
                )}
                {optionsType === 'checkbox' && (
                  <Checkbox
                    isChecked={(value?.filter((val) => val.id === option.id)?.length ?? 0) > 0}
                    label={option.text || `Image Answer Option ${index + 1}`}
                    onChange={toggleCheckbox}
                    value={option}
                  />
                )}
              </li>
            );
          })}
          {availableOptions?.length === 0 && isQuestion && (
            <li>
              No suitable questions available before this question (
              <strong>{QUESTION_TYPES.RADIO.name}</strong>,{' '}
              <strong>{QUESTION_TYPES.CHECKBOX.name}</strong>), or{' '}
              <strong>{QUESTION_TYPES.NUMERIC.name}</strong>.
            </li>
          )}
        </ul>
      </Fieldset>
    </PillOverlayPopover>
  );
};

QuestionLogicOptions.propTypes = {
  firstErrorRef: PropTypes.object,
  isCompleted: PropTypes.bool,
  isError: PropTypes.bool,
  isFocused: PropTypes.bool,
  isQuestion: PropTypes.bool,
  isQuotaGroup: PropTypes.bool,
  isQuotaGroupSelectionValid: PropTypes.bool,
  isRequired: PropTypes.bool,
  isSkip: PropTypes.bool,
  options: PropTypes.array.isRequired,
  optionsLabel: PropTypes.string.isRequired,
  optionsType: PropTypes.oneOf(['checkbox', 'radio']).isRequired,
  setValue: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export default QuestionLogicOptions;
